import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import UserLayout from '@/components/Layout/UserLayout';
import { Button, Callout, Card, ConfirmationDialog, FormInput, Modal, StatusBadge, Tabs } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { useAppQuery } from '@/hooks/useAppQuery';
import ActionMenu from './components/ActionMenu';
import CreateRunModal from './components/CreateRunModal';
import CreateVariantModal from './components/CreateVariantModal';
import StudyResultsPanel from './components/StudyResultsPanel';
import StudyRunsTable from './components/StudyRunsTable';
import StudyVariantsTable, { type StudyVariantActions } from './components/StudyVariantsTable';
import {
    buildIdeaLabRulesEditorPath,
    getRunStatusTone,
    isIdeaLabRunActive,
    parsePositiveInt,
    RUN_STATUS_LABELS,
    type IdeaLabRunSummary,
    type IdeaLabVariantSummary
} from './ideaLabUtils';
import { formatWallClockShort, parseStudyTab, summarizeStudyRuns, summarizeStudyVariants, type StudyTab } from './studyView';
import { useMakeVariation } from './useMakeVariation';

const StatTile = ({ label, children, detail }: { label: string; children: ReactNode; detail?: ReactNode }) => (
    <div className="min-w-0 rounded-lg border border-zinc-800 bg-[#1a1a1a] px-4 py-3">
        <div className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">{label}</div>
        <div className="mt-1 truncate text-base font-semibold text-zinc-100">{children}</div>
        {detail && <div className="mt-0.5 truncate text-xs text-zinc-500">{detail}</div>}
    </div>
);

const joinParts = (parts: (string | false | null)[]): string => parts.filter((part): part is string => Boolean(part)).join(' · ');

/**
 * One study as a dashboard: key stats, then tabs for the latest results (a scoreboard of one
 * completed run), the variant lineage tree, and the runs. The tab and the results run live in the
 * URL. Freezing and choosing the control happen here; the per-variant page carries the payload and
 * artifact history, and the run page carries the full report.
 */
const IdeaLabStudy = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const { studyId: studyIdParam } = useParams();
    const studyId = parsePositiveInt(studyIdParam ?? null);
    const activeTab = parseStudyTab(searchParams.get('tab'));
    const resultsRunId = parsePositiveInt(searchParams.get('run'));

    const [isAddVariantOpen, setIsAddVariantOpen] = useState(false);
    const [runFormSource, setRunFormSource] = useState<{ sourceRunId: number | null } | null>(null);
    const [freezeTarget, setFreezeTarget] = useState<IdeaLabVariantSummary | null>(null);
    const [archiveVariantTarget, setArchiveVariantTarget] = useState<IdeaLabVariantSummary | null>(null);
    const [cancelRunTarget, setCancelRunTarget] = useState<IdeaLabRunSummary | null>(null);
    const [confirmArchiveStudy, setConfirmArchiveStudy] = useState(false);
    const [isEditStudyOpen, setIsEditStudyOpen] = useState(false);
    const [editStudyName, setEditStudyName] = useState('');
    const [editStudyDescription, setEditStudyDescription] = useState('');

    const studyQuery = useAppQuery(
        {
            ...trpc.ideaLab.getStudy.queryOptions({ studyId: studyId ?? 0 }),
            enabled: studyId !== null
        },
        { fallbackErrorMessage: 'Failed to load the study' }
    );
    const runsQuery = useAppQuery(
        {
            ...trpc.ideaLab.listRuns.queryOptions({ studyId: studyId ?? 0 }),
            enabled: studyId !== null,
            refetchInterval: (query) => ((query.state.data ?? []).some(isIdeaLabRunActive) ? 5000 : false)
        },
        { fallbackErrorMessage: 'Failed to load runs' }
    );

    const study = studyQuery.data ?? null;
    const runs = useMemo(() => runsQuery.data ?? [], [runsQuery.data]);
    const variantStats = useMemo(() => summarizeStudyVariants(study?.variants ?? []), [study]);
    const runStats = useMemo(() => summarizeStudyRuns(runs), [runs]);

    const updateSearchParams = useCallback(
        (patch: Record<string, string | null>) => {
            const next = new URLSearchParams(searchParams);

            for (const [key, value] of Object.entries(patch)) {
                if (value === null) next.delete(key);
                else next.set(key, value);
            }

            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams]
    );
    const selectTab = (tab: StudyTab) => updateSearchParams({ tab: tab === 'results' ? null : tab });

    const invalidateStudy = () => queryClient.invalidateQueries(trpc.ideaLab.getStudy.pathFilter());

    const freezeMutation = useMutation({
        ...trpc.ideaLab.freezeVariant.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (result) => {
            const warnings = result.diagnostics.filter((diagnostic) => diagnostic.severity !== 'error').length;

            showToast(
                `Froze "${result.variant.label}"${warnings > 0 ? ` with ${warnings} compiler warning${warnings === 1 ? '' : 's'}` : ''}`,
                'success'
            );
            setFreezeTarget(null);
            await invalidateStudy();
        }
    });
    const setControlMutation = useMutation({
        ...trpc.ideaLab.setStudyControl.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (variant) => {
            showToast(`"${variant.label}" is now the study control`, 'success');
            await invalidateStudy();
        }
    });
    const archiveVariantMutation = useMutation({
        ...trpc.ideaLab.archiveVariant.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (variant) => {
            showToast(`Archived "${variant.label}"`, 'success');
            setArchiveVariantTarget(null);
            await invalidateStudy();
        }
    });
    const archiveStudyMutation = useMutation({
        ...trpc.ideaLab.archiveStudy.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (archived) => {
            showToast(`Archived study "${archived.name}"`, 'success');
            setConfirmArchiveStudy(false);
            await Promise.all([invalidateStudy(), queryClient.invalidateQueries(trpc.ideaLab.listStudies.pathFilter())]);
            void navigate('/user/ideaLab');
        }
    });
    const updateStudyMutation = useMutation({
        ...trpc.ideaLab.updateStudy.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (updated) => {
            showToast(`Updated study "${updated.name}"`, 'success');
            setIsEditStudyOpen(false);
            await Promise.all([invalidateStudy(), queryClient.invalidateQueries(trpc.ideaLab.listStudies.pathFilter())]);
        }
    });
    const openEditStudy = () => {
        if (!study) throw new Error('Cannot edit a study before it has loaded');

        setEditStudyName(study.name);
        setEditStudyDescription(study.description ?? '');
        setIsEditStudyOpen(true);
    };
    const variation = useMakeVariation();
    const cancelRunMutation = useMutation({
        ...trpc.ideaLab.cancelRun.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (run) => {
            showToast(`Cancelled run "${run.label}"`, 'success');
            setCancelRunTarget(null);
            await queryClient.invalidateQueries(trpc.ideaLab.listRuns.pathFilter());
        }
    });

    const studyIsOpen = study?.status === 'open';

    const variantActions: StudyVariantActions = {
        onEditRules: (variant) => void navigate(buildIdeaLabRulesEditorPath(variant.id)),
        onFreeze: setFreezeTarget,
        onMakeVariation: (source) => {
            if (!study) throw new Error('Cannot make a variation before the study has loaded');

            variation.makeVariation(
                source,
                study.variants.map((candidate) => candidate.variantKey)
            );
        },
        onSetControl: (variant) => setControlMutation.mutate({ studyId: variant.studyId, variantId: variant.id }),
        onArchive: setArchiveVariantTarget,
        isMakingVariation: variation.isPending,
        isSettingControl: setControlMutation.isPending
    };

    if (studyId === null) {
        return (
            <UserLayout containerSize="1400px" title="Idea Lab Study - Project 314">
                <Callout variant="error">Invalid study id.</Callout>
            </UserLayout>
        );
    }

    const latestRun = runStats.latest;
    const activeVariantCount = variantStats.total - variantStats.archived;

    return (
        <UserLayout containerSize="1400px" title={`${study?.name ?? `Study #${studyId}`} - Idea Lab - Project 314`}>
            <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                    <Link className="text-xs text-zinc-500 hover:text-zinc-300" to="/user/ideaLab">
                        ← Idea Lab
                    </Link>
                    <h1 className="text-2xl font-semibold text-zinc-100">
                        {study?.name ?? `Study #${studyId}`}
                        <span className="ml-2 align-middle text-sm font-normal text-zinc-500">#{studyId}</span>
                    </h1>
                    {study?.description && <p className="mt-1 max-w-4xl text-sm text-zinc-400">{study.description}</p>}
                </div>
                {study && (
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                        <StatusBadge status={study.status} tone={studyIsOpen ? 'success' : 'neutral'} />
                        {studyIsOpen && (
                            <>
                                <Button size="sm" variant="secondary" onClick={() => setIsAddVariantOpen(true)}>
                                    Add variant
                                </Button>
                                <Button size="sm" variant="primary" onClick={() => setRunFormSource({ sourceRunId: null })}>
                                    New run
                                </Button>
                                <ActionMenu
                                    items={[
                                        {
                                            label: 'Edit details',
                                            description: 'Rename the study or change its question',
                                            onSelect: openEditStudy
                                        },
                                        {
                                            label: 'Archive study',
                                            description: 'Keep it readable but stop new variants and runs',
                                            onSelect: () => setConfirmArchiveStudy(true),
                                            tone: 'danger'
                                        }
                                    ]}
                                    label="More study actions"
                                />
                            </>
                        )}
                    </div>
                )}
            </div>

            {studyQuery.isLoading ? (
                <DataLoadingSpinner text="Loading study..." />
            ) : studyQuery.error instanceof Error ? (
                <Callout variant="error">{studyQuery.error.message}</Callout>
            ) : study ? (
                <div className="space-y-5">
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                        <StatTile
                            detail={joinParts([
                                `${variantStats.frozen} frozen`,
                                variantStats.draft > 0 && `${variantStats.draft} ${variantStats.draft === 1 ? 'draft' : 'drafts'}`,
                                variantStats.promoted > 0 && `${variantStats.promoted} promoted`,
                                variantStats.archived > 0 && `${variantStats.archived} archived`
                            ])}
                            label="Variants"
                        >
                            {activeVariantCount}
                        </StatTile>
                        <StatTile
                            detail={
                                runsQuery.isLoading
                                    ? 'Loading…'
                                    : joinParts([
                                          runStats.active > 0 && `${runStats.active} in progress`,
                                          runStats.stopped > 0 && `${runStats.stopped} failed or cancelled`,
                                          runStats.active === 0 && runStats.stopped === 0 && `${runStats.total} total`
                                      ])
                            }
                            label="Completed runs"
                        >
                            {runsQuery.isLoading ? '—' : runStats.completed}
                        </StatTile>
                        <StatTile detail={variantStats.control?.variantKey ?? 'Set one from the Variants tab'} label="Control">
                            {variantStats.control ? (
                                <Link
                                    className="text-blue-300 hover:text-blue-200"
                                    title={variantStats.control.label}
                                    to={`/user/ideaLab/variant/${variantStats.control.id}`}
                                >
                                    {variantStats.control.label}
                                </Link>
                            ) : (
                                <span className="text-zinc-500">Not set</span>
                            )}
                        </StatTile>
                        <StatTile
                            detail={
                                latestRun ? (
                                    <span className="inline-flex items-center gap-2">
                                        <StatusBadge
                                            pulse={isIdeaLabRunActive(latestRun)}
                                            status={latestRun.status}
                                            tone={getRunStatusTone(latestRun.status)}
                                        >
                                            {RUN_STATUS_LABELS[latestRun.status]}
                                        </StatusBadge>
                                        {formatWallClockShort(latestRun.queuedAt ?? latestRun.createdAt)}
                                    </span>
                                ) : undefined
                            }
                            label="Latest run"
                        >
                            {latestRun ? (
                                <Link
                                    className="text-blue-300 hover:text-blue-200"
                                    title={latestRun.label}
                                    to={`/user/ideaLab/run/${latestRun.id}`}
                                >
                                    {latestRun.label}
                                </Link>
                            ) : (
                                <span className="text-zinc-500">None yet</span>
                            )}
                        </StatTile>
                    </div>

                    <Card bodyClassName="p-4 sm:p-5">
                        <Tabs
                            activeTab={activeTab}
                            tabs={[
                                {
                                    id: 'results',
                                    label: 'Results',
                                    content: runsQuery.isLoading ? (
                                        <DataLoadingSpinner text="Loading runs..." />
                                    ) : (
                                        <StudyResultsPanel
                                            canQueueRun={studyIsOpen}
                                            completedRuns={runStats.completedRuns}
                                            hasActiveRun={runStats.active > 0}
                                            isActive={activeTab === 'results'}
                                            selectedRunId={resultsRunId}
                                            variants={study.variants}
                                            onNewRun={() => setRunFormSource({ sourceRunId: null })}
                                            onSelectRun={(runId) => updateSearchParams({ run: String(runId) })}
                                        />
                                    )
                                },
                                {
                                    id: 'variants',
                                    label: `Variants (${activeVariantCount})`,
                                    content: (
                                        <StudyVariantsTable
                                            actions={variantActions}
                                            studyIsOpen={studyIsOpen}
                                            variants={study.variants}
                                            onAddVariant={() => setIsAddVariantOpen(true)}
                                        />
                                    )
                                },
                                {
                                    id: 'runs',
                                    label: `Runs (${runs.length})`,
                                    content: runsQuery.isLoading ? (
                                        <DataLoadingSpinner text="Loading runs..." />
                                    ) : (
                                        <StudyRunsTable
                                            runs={runs}
                                            studyIsOpen={studyIsOpen}
                                            onCancel={setCancelRunTarget}
                                            onNewRun={() => setRunFormSource({ sourceRunId: null })}
                                            onRunAgain={(run) => setRunFormSource({ sourceRunId: run.id })}
                                        />
                                    )
                                }
                            ]}
                            onTabChange={(tabId) => selectTab(parseStudyTab(tabId))}
                        />
                    </Card>
                </div>
            ) : null}

            {study && (
                <>
                    <Modal
                        footer={
                            <div className="flex justify-end gap-2">
                                <Button variant="secondary" onClick={() => setIsEditStudyOpen(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    disabled={editStudyName.trim().length === 0 || updateStudyMutation.isPending}
                                    isLoading={updateStudyMutation.isPending}
                                    variant="primary"
                                    onClick={() =>
                                        updateStudyMutation.mutate({
                                            studyId: study.id,
                                            name: editStudyName.trim(),
                                            description: editStudyDescription.trim() || null
                                        })
                                    }
                                >
                                    Save
                                </Button>
                            </div>
                        }
                        isOpen={isEditStudyOpen}
                        title="Edit study details"
                        onClose={() => setIsEditStudyOpen(false)}
                    >
                        <div className="space-y-4">
                            <FormInput
                                label="Name"
                                maxLength={255}
                                noMargin
                                value={editStudyName}
                                onChange={(event) => setEditStudyName(event.target.value)}
                            />
                            <label className="block text-sm text-zinc-300">
                                <span className="mb-1 block">Description</span>
                                <textarea
                                    className="min-h-28 w-full rounded border border-zinc-700 bg-zinc-900 px-3 py-2 text-zinc-100 focus:border-blue-500 focus:outline-none"
                                    maxLength={4000}
                                    value={editStudyDescription}
                                    onChange={(event) => setEditStudyDescription(event.target.value)}
                                />
                            </label>
                        </div>
                    </Modal>
                    <CreateVariantModal
                        isOpen={isAddVariantOpen}
                        studyId={study.id}
                        variants={study.variants}
                        onClose={() => setIsAddVariantOpen(false)}
                        onCreated={() => setIsAddVariantOpen(false)}
                    />
                    <CreateRunModal
                        isOpen={runFormSource !== null}
                        sourceRunId={runFormSource?.sourceRunId ?? null}
                        study={study}
                        onClose={() => setRunFormSource(null)}
                        onCreated={(run) => {
                            setRunFormSource(null);
                            void navigate(`/user/ideaLab/run/${run.id}`);
                        }}
                    />
                </>
            )}

            <ConfirmationDialog
                confirmText={freezeMutation.isPending ? 'Freezing…' : 'Freeze'}
                confirmVariant="primary"
                isOpen={freezeTarget !== null}
                message={
                    freezeTarget
                        ? `Freeze "${freezeTarget.label}"? The rules are compiled into an immutable artifact and the variant can no longer be edited. Make a variation to try a further change.`
                        : ''
                }
                title="Freeze variant"
                onCancel={() => setFreezeTarget(null)}
                onConfirm={() => freezeTarget && !freezeMutation.isPending && freezeMutation.mutate({ variantId: freezeTarget.id })}
            />
            <ConfirmationDialog
                confirmText={archiveVariantMutation.isPending ? 'Archiving…' : 'Archive'}
                confirmVariant="danger"
                isOpen={archiveVariantTarget !== null}
                message={
                    archiveVariantTarget
                        ? `Archive "${archiveVariantTarget.label}"? It stays readable, runs already queued still evaluate its frozen artifact, but it cannot join new runs.`
                        : ''
                }
                title="Archive variant"
                onCancel={() => setArchiveVariantTarget(null)}
                onConfirm={() =>
                    archiveVariantTarget &&
                    !archiveVariantMutation.isPending &&
                    archiveVariantMutation.mutate({ variantId: archiveVariantTarget.id })
                }
            />
            <ConfirmationDialog
                confirmText={archiveStudyMutation.isPending ? 'Archiving…' : 'Archive study'}
                confirmVariant="danger"
                isOpen={confirmArchiveStudy}
                message="Archive this study? Its variants and runs stay readable, but no new variants or runs can be added."
                title="Archive study"
                onCancel={() => setConfirmArchiveStudy(false)}
                onConfirm={() => study && !archiveStudyMutation.isPending && archiveStudyMutation.mutate({ studyId: study.id })}
            />
            <ConfirmationDialog
                confirmText={cancelRunMutation.isPending ? 'Cancelling…' : 'Cancel run'}
                confirmVariant="danger"
                isOpen={cancelRunTarget !== null}
                message={
                    cancelRunTarget
                        ? `Cancel "${cancelRunTarget.label}"? Pending work is dropped; work already claimed by a worker finishes and is discarded.`
                        : ''
                }
                title="Cancel run"
                onCancel={() => setCancelRunTarget(null)}
                onConfirm={() => cancelRunTarget && !cancelRunMutation.isPending && cancelRunMutation.mutate({ runId: cancelRunTarget.id })}
            />
        </UserLayout>
    );
};

export default IdeaLabStudy;
